import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
interface MobileNavigationContextType {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  toggleIsOpen: (isOpen: boolean) => void;
}

interface MobileNavigationProviderProps {
  children: ReactNode;
  navigationElementId: string;
}

const MobileNavigationContext = createContext<MobileNavigationContextType | undefined>(undefined);

export function MobileNavigationProvider({ children, navigationElementId }: MobileNavigationProviderProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  function toggleIsOpen() {
    setIsOpen((previousIsOpen) => !previousIsOpen);
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const element = document.getElementById(navigationElementId);
      const toggleElement = document.querySelector(`[aria-controls=${navigationElementId}]`);

      if (element && !element.contains(event.target as Node) && !toggleElement?.contains(event.target as Node)) {
        if (isOpen) {
          setIsOpen(false);
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, navigationElementId]);

  return (
    <MobileNavigationContext.Provider value={{ isOpen, setIsOpen, toggleIsOpen }}>
      {children}
    </MobileNavigationContext.Provider>
  );
}

export const useMobileNavigation = (): MobileNavigationContextType => {
  const context = useContext(MobileNavigationContext);

  if (context === undefined) {
    throw new Error('useMobileNavigation must be used within a MobileNavigationProvider');
  }
  return context;
};
